<script setup>
import { ref, computed, onMounted, onBeforeMount } from "vue";
import { useMainStore } from "@/stores/store.js";
import { useRouter, useRoute } from "vue-router";
import {
	fetchMaterialNrcData,
	fetchProductData,
	fetchProductsDataExtended,
	fetchSurfaceNrcData,
} from "@/data.js";

import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";

const store = useMainStore();
const router = useRouter();
const route = useRoute();

$log.alwayslog(
	`[App:init] import.meta.env.MODE: ${JSON.stringify(import.meta.env.MODE, null, 4)}`,
);

/*
------------------------------------------------------------------------
Ensures the current route view matches the  URL (for a page reload or bookmark).
If the current URL does not match the route view, we switch it over.
Note: For some reason, the setTimeout makes this behave predictably.
Without it route.name is not always picked up on hard refresh.
------------------------------------------------------------------------
*/
const ready = ref(false);

/*
------------------------------------------------------------------------
Fetch any data required for the app to be able to run.
------------------------------------------------------------------------
*/
const loadData = async () => {
	$log.log(`[App:loadData] Loading data`);

	try {
		const productData = await fetchProductData();
		const productsDataExtended = await fetchProductsDataExtended();

		// $log.log(`loadData : productsDataExtended : ${JSON.stringify(productsDataExtended, null, 4)}`);

		// Store products data independently - this is CRITICAL for core functionality
		// The app cannot function without products catalog, so we must store it even if extended data fails
		if (productData) {
			store.$patch({
				products: [...productData],
			});
		} else {
			$log.log(
				`[App:loadData] ERROR: Failed to load products data - app may not function correctly`,
			);
		}

		// Store productsExtended data independently - this is OPTIONAL for extended features
		// The app can function without extended data (just without lumen specs, etc.)
		if (productsDataExtended) {
			store.$patch({
				productsExtended: productsDataExtended, // This is an object, not an array
			});
		} else {
			$log.log(
				`[App:loadData] WARNING: Failed to load productsExtended data - extended features may not be available`,
			);
		}

		// $log.log(`store.productsExtended : ${JSON.stringify(store.productsExtended, null, 4)}`);
	} catch (error) {
		$log.log(`[App:loadData] Something went wrong loading data`);
		console.error("Data loading error:", error);
	}

	// const materialNrcData = await fetchMaterialNrcData();
	// if (materialNrcData) {
	//     store.$patch({
	//         materialnrc: [...materialNrcData],
	//     });
	// }

	// Surface NRC: merge imported NRC values into the store defaults.
	// The store defaults (store-state.js) are the source of truth for
	// structure (identifiers, surfaces, labels). The spreadsheet import
	// only updates NRC numbers — unrecognised identifiers are ignored,
	// missing identifiers keep their fallback NRC.
	const surfaceNrcData = await fetchSurfaceNrcData();
	if (surfaceNrcData) {
		const importedByIdentifier = Object.fromEntries(
			surfaceNrcData.map((entry) => [entry.identifier, entry.nrc]),
		);
		const knownIdentifiers = store.surfaceNrc.map((e) => e.identifier);

		// Warn about imported identifiers that don't match any known entry
		for (const id of Object.keys(importedByIdentifier)) {
			if (!knownIdentifiers.includes(id)) {
				console.warn(
					`[App:loadData] Surface NRC import: unknown identifier "${id}" — not matched to any known entry, skipping.`,
				);
			}
		}

		// Warn about known identifiers missing from the import
		for (const id of knownIdentifiers) {
			if (importedByIdentifier[id] === undefined) {
				console.warn(
					`[App:loadData] Surface NRC import: identifier "${id}" not found in imported data — using fallback NRC.`,
				);
			}
		}

		const merged = store.surfaceNrc.map((entry) => {
			const importedNrc = importedByIdentifier[entry.identifier];
			return importedNrc !== undefined
				? { ...entry, nrc: importedNrc }
				: entry;
		});
		store.$patch({ surfaceNrc: merged });
	}
};

/*
------------------------------------------------------------------------
If the current step is "report" that means the user has already generated
a report and the page has been refreshed. If so, we reset the data and
go back to the first step.
------------------------------------------------------------------------
*/
onBeforeMount(() => {
	// Report generation is a single-session action — always reset on page load.
	// This prevents stale reportGenerated/triggerDocGen flags in localStorage
	// from hiding the footer nav or blocking UI after a refresh.
	store.user.reportGenerated = false;
	store.triggerDocGen = false;

	if (import.meta.env.MODE === "production") {
		if (store.user.currentStepName === "Report") {
			store.resetUserData(false);
			store.gotoStep("Start", router, "previous");
		}
	}
});

onMounted(async () => {
	store.overlays.loading = true;

	ready.value = false;
	await loadData();

	// Load product data for ALL products (always refresh from JSON to ensure latest structure)
	// This is important because:
	// 1. Report step renders multiple ProductCards that need product data
	// 2. Product data structure may have changed since it was saved to localStorage
	// 3. Size keys in localStorage might not match current product catalog
	//
	// We also validate that the stored size exists in the product data, and reset if not.
	store.user.products.forEach((product) => {
		// Always refresh product data from catalog to ensure latest structure
		if (product && product.product !== "") {
			const productData = store.products.find((p) => p.id === product.product);
			if (productData) {
				const updates = { data: productData };

				// Validate that stored size exists in product data, reset if not
				const availableSizes = Object.keys(productData.sizes?.items || {});
				if (product.size && !availableSizes.includes(product.size)) {
					updates.size = "";
					updates.size_variant = "";
					updates.metric_sabins = "";
				}

				store.updateProduct(product.id, updates);
			}
		}
	});

	store.overlays.product.open = false;
	const storeCurrentStepName = store.user.currentStepName;
	let gotoRouteName = "";
	$log.log(
		`[App:onMounted] storeCurrentStepName: ${JSON.stringify(storeCurrentStepName, null, 4)}`,
	);
	$log.log(`[App:onMounted] route.name: ${JSON.stringify(route.name, null, 4)}`);

	if (store.user.reset) {
		// User's localstorage data has been reset so we always want to go back to Start
		gotoRouteName = "Start";
	} else if (store.user.currentStepName !== "") {
		// Otherwise go to the current step as per store/localstorage
		gotoRouteName = store.user.currentStepName;
	} else {
		// Always fall back to Start
		gotoRouteName = "Start";
	}

	store.gotoStep(gotoRouteName, router, "previous");

	store.overlays.loading = false;
	ready.value = true;
});
</script>

<template>
	<form>
		<input type="hidden" name="instance_id" v-model="store.instance_id" />
		<div class="min-w-[350px] bg-page md:pt-[112px]">
			<div class="flex h-dvh flex-col">
				<div
					id="app-header-wrapper"
					class="z-40 flex-none bg-page text-center md:fixed md:left-0 md:right-0 md:top-0"
				>
					<AppHeader />
				</div>
				<div
					class="lux-main z-20 mx-auto w-full max-w-[1500px]"
					:class="
						store.getCurrentStepName === 'Report' ? 'lux-main-no-padd' : ''
					"
				>
					<div class="relative">
						<router-view></router-view>
						<!-- <div v-if="ready">
                            <router-view v-slot="{ Component, route }">
                                <transition name="stepfade">
                                    <component
                                        :is="Component"
                                        v-if="Component"
                                        :key="route.fullPath" />
                                </transition>
                            </router-view>
                        </div>
                        <div v-else>
                            Not ready
                        </div> -->
					</div>
				</div>
			</div>
		</div>
		<div
			id="app-footer-wrapper"
			class="fixed bottom-0 left-0 right-0 top-auto z-200 flex-none bg-navoverlay text-center"
		>
			<AppFooter />
		</div>
		<!--
        OVERLAY MODAL BACKDROP
        -->
		<transition name="stepfade">
			<Teleport to="body">
				<div
					class="fixed bottom-0 left-0 right-0 top-0 z-[100] bg-blacka75"
					v-if="
						store.overlays.product.open ||
						store.overlays.productvalidation.open ||
						store.overlays.reporterror.open ||
						store.overlays.loading
					"
				></div>
			</Teleport>
		</transition>
	</form>
</template>
